/**
 * Session Store
 *
 * Connect-RPC session_id 로컬 저장소
 * - session_id 저장 및 조회
 * - Connect-RPC 서비스에 session_id 동기화
 */

import { create } from 'zustand';

/** 세션 스트림(탭 통합 연결)의 상태 — 끊겨 재수립 중이면 화면이 공백을 상태로 보인다(토스트가 아니라 상태) */
export type StreamLink = 'connected' | 'reconnecting';

/** 인증 갱신을 기다리는 자리 — 세션 스트림·실행 중 값 폴링이 인증 만료(`transportMeaning` 'auth-expired')를 받으면 들고, 다시 닿거나
 *  멈추면 난다(D-105). 하나라도 기다리면 실행 화면이 «인증 서버 응답 대기 중» 을 보인다 — 숨기지 않는다(2026-10-27 결정) */
export type AuthWaiter = 'session-stream' | 'icv-poll' | 'fmu-poll';

interface SessionState {
  // State
  sessionId: string | null;
  sessionCreating: boolean;
  streamLink: StreamLink;
  authWaiters: ReadonlySet<AuthWaiter>;

  // Actions
  setSessionId: (sessionId: string | null) => void;
  setSessionCreating: (creating: boolean) => void;
  setStreamLink: (link: StreamLink) => void;
  /** 인증 갱신을 기다리기 시작한다 / 끝낸다 — 같은 자리를 두 번 들거나 나도 한 번이다 */
  beginAuthWait: (who: AuthWaiter) => void;
  endAuthWait: (who: AuthWaiter) => void;
  clearSessionId: () => void;
  getSessionId: () => string | null;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  // Initial state
  sessionId: null,
  sessionCreating: false,
  streamLink: 'connected',
  authWaiters: new Set(),

  /**
   * Session ID 설정
   * Connect-RPC 서비스에도 동기화
   */
  setSessionId: (sessionId: string | null) => {
    console.log('[SessionStore] Setting session ID:', sessionId);

    // 단일 소스: sessionStore만 갱신
    set({ sessionId });
  },

  setSessionCreating: (creating: boolean) => {
    set({ sessionCreating: creating });
  },

  setStreamLink: (link: StreamLink) => {
    set((state) => (state.streamLink === link ? state : { streamLink: link }));
  },

  beginAuthWait: (who) => {
    set((state) => (state.authWaiters.has(who) ? state : { authWaiters: new Set([...state.authWaiters, who]) }));
  },

  endAuthWait: (who) => {
    set((state) => (state.authWaiters.has(who) ? { authWaiters: new Set([...state.authWaiters].filter((w) => w !== who)) } : state));
  },

  /**
   * Session ID 클리어
   */
  clearSessionId: () => {
    const currentSessionId = get().sessionId;

    if (currentSessionId) {
      console.log('[SessionStore] Clearing session ID:', currentSessionId);
    }

    // 단일 소스: sessionStore만 갱신
    set({ sessionId: null, sessionCreating: false, streamLink: 'connected', authWaiters: new Set() });
  },

  /**
   * 현재 Session ID 가져오기
   */
  getSessionId: () => {
    return get().sessionId;
  },
}));

/** 인증 기다림에 들거나 난다 — 폴링 계수기(`createTransientTracker` 의 onAuthWait)가 부른다 */
export const noteAuthWait = (who: AuthWaiter) => (waiting: boolean): void => {
  const state = useSessionStore.getState();
  if (waiting) state.beginAuthWait(who); else state.endAuthWait(who);
};
